import { type AxiosInstance, type AxiosResponse } from 'axios'
import { useMemo } from 'react'
import { type Arguments as SWRArguments, type SWRConfiguration, type Key as SWRKey } from 'swr'
import useSWRMutation, { type SWRMutationConfiguration } from 'swr/mutation'
import { axiosClientRegistry } from '../http/axios/axiosClientRegistry'
import { type ClientNames } from '../http/types'
import { serializeKey, type Key } from './serializeKey'

export type Fetcher<URL extends SWRArguments, Data, ExtraArgs = undefined> = (url: URL, options: { arg: ExtraArgs, client: AxiosInstance }) => Promise<AxiosResponse<Data>>

export type MutationConfiguration<Data, Error, Key extends SWRKey, ExtraArgs> = SWRMutationConfiguration<Data, Error, Key, ExtraArgs> & {
  use?: SWRConfiguration<Data, Error>['use']
}

export interface CreateMutationHelperConfig<URL extends SWRArguments, Data, ExtraArgs, Params> {
  /**
   * client 是用來定義這個 api 會使用哪一個 client 來發送 request
   */
  client: ClientNames
  /**
   * key 也就是 params serialize to key 的 function 是用來定義這個 api 所需要的 Params 並回傳這組 key 給 swr 使用
   */
  key: Key<Params, SWRArguments>
  /**
   * Mutation fetcher to send request, it provides the url and extra args and client for post/delete/patch etc.
   */
  fetcher: Fetcher<URL, Data, ExtraArgs>
  /**
   * Set true to use mock client.
   */
  mock?: boolean
}

export function createMutationHelper<URL extends SWRArguments, Data, ExtraArgs = undefined, Params = undefined, Error = unknown>(config: CreateMutationHelperConfig<URL, Data, ExtraArgs, Params>) {
  const { key: _key, fetcher, client: clientName = 'fortune', mock } = config

  const useMutation = (params?: Params, options?: MutationConfiguration<AxiosResponse<Data>, Error, typeof _key, ExtraArgs>) => {
    const key = useMemo(() => serializeKey(_key, params), [params])

    const response = useSWRMutation(key, (url: URL, options: { arg: ExtraArgs }) => {
      const client = axiosClientRegistry.getClient(clientName, mock)
      return fetcher(url, {
        arg: options.arg,
        client,
      })
    }, options)

    // TODO: Should update axios to latest version and implement API canceling.

    return response
  }

  return {
    /**
     * 回傳給應用層使用的 hook，目前僅接受兩個參數，params, swrOptions，未來可能會多一個 axios options 目前使用上還沒碰到如果有需要再加
     * 另外也有保留 mutation 可以傳 url parameter 的彈性，可以在 api url 中帶入參數
     */
    useMutation,
  }
}
